import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check authentication status on mount
    const checkAuth = useCallback(async () => {
        const token = localStorage.getItem('session_token');
        if (!token) {
            setIsLoading(false);
            return;
        }
        
        try {
            const response = await fetch(`${API_URL}/api/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include'
            });
            
            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                setIsAuthenticated(true);
            } else {
                // Token invalid, clear it
                localStorage.removeItem('session_token');
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Register with email/password
    const register = async (email, password, name) => {
        const response = await fetch(`${API_URL}/api/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password, name })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Registration failed');
        }

        const data = await response.json();
        // Store token for API calls
        localStorage.setItem('session_token', data.session_token);
        setUser(data);
        setIsAuthenticated(true);
        return data;
    };

    // Login with email/password
    const login = async (email, password) => {
        const response = await fetch(`${API_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ email, password })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Login failed');
        }

        const data = await response.json();
        localStorage.setItem('session_token', data.session_token);
        setUser(data);
        setIsAuthenticated(true);
        return data;
    };

    // Login with Google OAuth
    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    // Handle Google OAuth callback
    const handleGoogleCallback = async (sessionId) => {
        const response = await fetch(`${API_URL}/api/auth/google/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Google authentication failed');
        }

        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        return userData;
    };

    // Logout
    const logout = async () => {
        try {
            await fetch(`${API_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Logout error:', error);
        }
        
        localStorage.removeItem('session_token');
        setUser(null);
        setIsAuthenticated(false);
    };

    // Update profile
    const updateProfile = async (updates) => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/profile`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify(updates)
        });

        if (!response.ok) {
            throw new Error('Failed to update profile');
        }

        const updatedUser = await response.json();
        setUser(updatedUser);
        return updatedUser;
    };

    // Get user orders
    const getOrders = async () => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/orders`, {
            headers: {
                'Authorization': `Bearer ${token}`
            },
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error('Failed to fetch orders');
        }

        return response.json();
    };

    // Address management
    const getAddresses = async () => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/addresses`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to fetch addresses');
        return response.json();
    };

    const addAddress = async (address) => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/addresses`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify(address)
        });
        if (!response.ok) throw new Error('Failed to add address');
        const newAddress = await response.json();
        await checkAuth(); // Refresh user data
        return newAddress;
    };

    const updateAddress = async (addressId, address) => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/addresses/${addressId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify(address)
        });
        if (!response.ok) throw new Error('Failed to update address');
        await checkAuth();
        return response.json();
    };

    const deleteAddress = async (addressId) => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/addresses/${addressId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to delete address');
        await checkAuth();
    };

    // Favorites sync
    const syncFavorites = async (localFavorites) => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/favorites/sync`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({ favorites: localFavorites })
        });
        if (!response.ok) throw new Error('Failed to sync favorites');
        const data = await response.json();
        await checkAuth();
        return data.favorites;
    };

    const addFavorite = async (itemId) => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/favorites/${itemId}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to add favorite');
        await checkAuth();
    };

    const removeFavorite = async (itemId) => {
        const token = localStorage.getItem('session_token');
        const response = await fetch(`${API_URL}/api/user/favorites/${itemId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Failed to remove favorite');
        await checkAuth();
    };

    const value = {
        user,
        isLoading,
        isAuthenticated,
        register,
        login,
        loginWithGoogle,
        handleGoogleCallback,
        logout,
        updateProfile,
        getOrders,
        getAddresses,
        addAddress,
        updateAddress,
        deleteAddress,
        syncFavorites,
        addFavorite,
        removeFavorite,
        checkAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
